import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type {
  AdminFailedDeduction,
  FailedDeductionResolution,
  FailedDeductionResolvedFilter,
} from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import {
  useFailedDeductionsList,
  useResolveFailedDeduction,
} from '../api/useAdmin.ts';
import { formatUsdCents } from '../components/formatters.ts';
import { Modal } from '../components/Modal.tsx';
import { useToast, coerceErrorMessage } from '../components/Toast.tsx';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';

/**
 * `/failed-deductions` — ops queue for `billing_failed_deductions`.
 *
 * Phase-1 contract: resolution is AUDIT ONLY. `Retry` marks a row
 * resolved but does NOT re-attempt the debit — it signals "I handled this
 * out-of-band, close the ticket". A tooltip on the `Retry` radio surfaces
 * this explicitly so operators don't assume the platform will re-charge.
 *
 * Filter tabs: `Unresolved` (default) / `Resolved` / `All`. Each tab
 * backs a separate React Query cache slot keyed by the filter value.
 */

const RESOLUTIONS: readonly FailedDeductionResolution[] = [
  'forgive',
  'chargeback',
  'retry',
];

const FILTER_TABS: readonly {
  readonly value: FailedDeductionResolvedFilter;
  readonly labelKey: I18nKey;
  readonly hintKey: I18nKey;
}[] = [
  {
    value: 'unresolved',
    labelKey: 'failed_deductions.filter.unresolved',
    hintKey: 'failed_deductions.filter.unresolved.hint',
  },
  {
    value: 'resolved',
    labelKey: 'failed_deductions.filter.resolved',
    hintKey: 'failed_deductions.filter.resolved.hint',
  },
  {
    value: 'all',
    labelKey: 'failed_deductions.filter.all',
    hintKey: 'failed_deductions.filter.all.hint',
  },
];

const PAGE_LIMIT = 50;

function formatRelative(iso: string): string {
  const parsed = Date.parse(iso);
  if (!Number.isFinite(parsed)) return '—';
  const delta = Date.now() - parsed;
  const seconds = Math.round(delta / 1000);
  if (seconds < 60) return `${Math.max(seconds, 0)}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

function truncateId(value: string, head = 8, tail = 4): string {
  if (value.length <= head + tail + 3) return value;
  return `${value.slice(0, head)}…${value.slice(-tail)}`;
}

function isResolvedFilter(
  value: string,
): value is FailedDeductionResolvedFilter {
  return value === 'all' || value === 'resolved' || value === 'unresolved';
}

function isResolution(value: string): value is FailedDeductionResolution {
  return value === 'forgive' || value === 'chargeback' || value === 'retry';
}

interface RowProps {
  readonly row: AdminFailedDeduction;
  readonly zebra: 'odd' | 'even';
  readonly onResolve: (row: AdminFailedDeduction) => void;
  readonly resolveLabel: string;
  readonly closedLabel: string;
}

function FailedDeductionRow({
  row,
  zebra,
  onResolve,
  resolveLabel,
  closedLabel,
}: RowProps): JSX.Element {
  const zebraClass = zebra === 'odd' ? 'bg-surface-container' : 'bg-surface-container-low';
  const accountPath = `/customers/accounts/${encodeURIComponent(row.account_id)}`;
  return (
    <tr
      data-testid={`failed-deduction-row-${row.id}`}
      className={`${zebraClass} transition-colors hover:bg-surface-bright`}
    >
      <td className="px-4 py-3 font-mono text-[12px]">
        <Link
          to={accountPath}
          title={row.account_id}
          data-testid={`failed-deduction-row-${row.id}-account`}
          className="text-on-surface-variant hover:text-primary focus-visible:outline-none focus-visible:underline"
        >
          {truncateId(row.account_id)}
        </Link>
      </td>
      <td
        className="px-4 py-3 font-mono text-[12px] text-on-surface-variant"
        title={row.request_id}
        data-testid={`failed-deduction-row-${row.id}-request`}
      >
        {truncateId(row.request_id)}
      </td>
      <td
        className="px-4 py-3 text-right font-mono text-[13px] font-semibold text-error"
        data-testid={`failed-deduction-row-${row.id}-amount`}
      >
        -{formatUsdCents(Math.abs(row.amount_usd_cents))}
      </td>
      <td className="px-4 py-3 text-[12px] text-on-surface">{row.reason ?? '—'}</td>
      <td className="px-4 py-3 text-[12px] text-on-surface-variant">
        {formatRelative(row.created_at)}
      </td>
      <td
        className="px-4 py-3 text-[12px] text-on-surface-variant"
        data-testid={`failed-deduction-row-${row.id}-resolved-at`}
      >
        {row.resolved_at === null ? '—' : formatRelative(row.resolved_at)}
      </td>
      <td className="px-4 py-3 text-right">
        {row.resolved_at === null ? (
          <button
            type="button"
            onClick={() => onResolve(row)}
            data-testid={`failed-deduction-row-${row.id}-resolve`}
            className="inline-flex items-center justify-center rounded-lg px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-primary transition-colors hover:bg-surface-container-highest focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container"
          >
            {resolveLabel}
          </button>
        ) : (
          <span className="inline-flex items-center rounded-full bg-surface-container-highest px-3 py-1 text-[10px] font-bold uppercase tracking-[0.08em] text-outline">
            {closedLabel}
          </span>
        )}
      </td>
    </tr>
  );
}

export function FailedDeductions(): JSX.Element {
  const t = useT();
  const [filter, setFilter] = useState<FailedDeductionResolvedFilter>('unresolved');
  const [offset, setOffset] = useState(0);
  const [resolveTarget, setResolveTarget] = useState<AdminFailedDeduction | null>(null);

  const query = useFailedDeductionsList(filter, { limit: PAGE_LIMIT, offset });

  const total = query.data?.total ?? 0;
  const currentOffset = query.data?.offset ?? offset;
  const currentLimit = query.data?.limit ?? PAGE_LIMIT;
  const rows = useMemo(() => query.data?.failed_deductions ?? [], [query.data]);
  const hasPrev = currentOffset > 0;
  const hasNext = currentOffset + currentLimit < total;

  function handleFilterChange(next: FailedDeductionResolvedFilter): void {
    setFilter(next);
    setOffset(0);
    setResolveTarget(null);
  }

  return (
    <section data-testid="failed-deductions-page" className="flex flex-col gap-8">
      <header className="flex flex-col gap-2">
        <h2 className="text-3xl font-semibold tracking-tight text-on-surface">
          {t('failed_deductions.title')}
        </h2>
        <p className="text-sm text-outline">{t('failed_deductions.subtitle')}</p>
      </header>

      <nav
        aria-label={t('failed_deductions.title')}
        data-testid="failed-deductions-tabs"
        className="flex flex-wrap items-center gap-2 rounded-xl bg-surface-container-low p-2"
      >
        {FILTER_TABS.map((tab) => {
          const isActive = tab.value === filter;
          return (
            <button
              key={tab.value}
              type="button"
              aria-pressed={isActive}
              onClick={() => handleFilterChange(tab.value)}
              data-testid={`failed-deductions-tab-${tab.value}`}
              title={t(tab.hintKey)}
              className={[
                'inline-flex items-center justify-center rounded-lg px-4 py-2 text-[11px] font-bold uppercase tracking-[0.08em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                isActive
                  ? 'bg-surface-container-highest text-primary'
                  : 'text-outline hover:bg-surface-container hover:text-on-surface',
              ].join(' ')}
            >
              {t(tab.labelKey)}
            </button>
          );
        })}
      </nav>

      {query.isError ? (
        <div
          role="alert"
          data-testid="failed-deductions-error"
          className="flex items-center justify-between gap-4 rounded-xl bg-error-container px-6 py-4 text-on-error-container"
        >
          <div className="flex flex-col gap-1">
            <span className="text-sm font-semibold">{t('failed_deductions.error.title')}</span>
            <span className="text-xs">
              {query.error instanceof OpsApiError
                ? `${query.error.code}: ${query.error.message}`
                : t('common.network_error')}
            </span>
          </div>
          <button
            type="button"
            onClick={() => void query.refetch()}
            data-testid="failed-deductions-retry"
            className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-4 py-2 text-[11px] font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-error-container"
          >
            {t('common.retry')}
          </button>
        </div>
      ) : null}

      {query.isPending ? (
        <div
          data-testid="failed-deductions-loading"
          className="flex flex-col gap-2 rounded-xl bg-surface-container p-4"
        >
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-12 animate-pulse rounded-lg bg-surface-container-low"
              aria-hidden="true"
            />
          ))}
          <span className="sr-only">{t('failed_deductions.loading_aria')}</span>
        </div>
      ) : null}

      {query.isSuccess && rows.length === 0 ? (
        <div
          data-testid="failed-deductions-empty"
          className="flex flex-col items-center gap-4 rounded-xl bg-surface-container px-8 py-16 text-center"
        >
          <h3 className="text-2xl font-semibold tracking-tight text-on-surface">
            {filter === 'unresolved'
              ? t('failed_deductions.empty.unresolved')
              : filter === 'resolved'
                ? t('failed_deductions.empty.resolved')
                : t('failed_deductions.empty.all')}
          </h3>
          <p className="max-w-md text-sm text-outline">{t('failed_deductions.empty.hint')}</p>
        </div>
      ) : null}

      {query.isSuccess && rows.length > 0 ? (
        <>
          <div className="overflow-hidden rounded-xl bg-surface-container">
            <table
              data-testid="failed-deductions-table"
              className="w-full border-separate border-spacing-0"
            >
              <thead>
                <tr className="bg-surface-container-high">
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant"
                  >
                    {t('failed_deductions.column.account')}
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant"
                  >
                    {t('failed_deductions.column.request_id')}
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant"
                  >
                    {t('failed_deductions.column.amount')}
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant"
                  >
                    {t('failed_deductions.column.reason')}
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant"
                  >
                    {t('failed_deductions.column.created')}
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant"
                  >
                    {t('failed_deductions.column.resolved')}
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-right text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant"
                  >
                    {t('failed_deductions.column.actions')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <FailedDeductionRow
                    key={row.id}
                    row={row}
                    zebra={i % 2 === 0 ? 'odd' : 'even'}
                    onResolve={(target) => setResolveTarget(target)}
                    resolveLabel={t('failed_deductions.row.resolve')}
                    closedLabel={t('failed_deductions.row.closed')}
                  />
                ))}
              </tbody>
            </table>
          </div>
          <nav
            aria-label={t('accounts.pagination.aria_label')}
            data-testid="failed-deductions-pagination"
            className="flex items-center justify-between gap-4 rounded-xl bg-surface-container-low px-6 py-4 text-[12px] text-outline"
          >
            <span>
              {t('audit.pagination.range', {
                start: currentOffset + 1,
                end: Math.min(currentOffset + rows.length, total),
                total,
              })}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={!hasPrev || query.isFetching}
                onClick={() => setOffset(Math.max(0, offset - PAGE_LIMIT))}
                data-testid="failed-deductions-pagination-prev"
                className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40 disabled:pointer-events-none"
              >
                {t('common.previous')}
              </button>
              <button
                type="button"
                disabled={!hasNext || query.isFetching}
                onClick={() => setOffset(offset + PAGE_LIMIT)}
                data-testid="failed-deductions-pagination-next"
                className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40 disabled:pointer-events-none"
              >
                {t('common.next')}
              </button>
            </div>
          </nav>
        </>
      ) : null}

      {resolveTarget !== null ? (
        <ResolveModal
          row={resolveTarget}
          onClose={() => setResolveTarget(null)}
        />
      ) : null}
    </section>
  );
}

interface ResolveModalProps {
  readonly row: AdminFailedDeduction;
  readonly onClose: () => void;
}

function ResolveModal({ row, onClose }: ResolveModalProps): JSX.Element {
  const t = useT();
  const resolve = useResolveFailedDeduction();
  const toast = useToast();
  const [choice, setChoice] = useState<FailedDeductionResolution>('forgive');
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    try {
      await resolve.mutateAsync({ id: row.id, body: { resolution: choice } });
      onClose();
    } catch (err) {
      const message =
        err instanceof OpsApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    }
  }

  return (
    <Modal
      isOpen
      onClose={() => {
        if (!resolve.isPending) onClose();
      }}
      titleId="resolve-failed-deduction-title"
      testId="failed-deduction-resolve-modal"
    >
      <form className="flex flex-col gap-6 p-8" onSubmit={handleSubmit} noValidate>
        <header className="flex flex-col gap-2">
          <h2
            id="resolve-failed-deduction-title"
            className="text-base font-semibold text-on-surface"
          >
            {t('failed_deductions.resolve_modal.title')}
          </h2>
          <p className="text-sm text-outline">
            {t('failed_deductions.resolve_modal.body_prefix')}
            <span className="font-mono font-semibold text-on-surface">
              {truncateId(row.account_id)}
            </span>
            {t('failed_deductions.resolve_modal.body_separator')}
            <span className="font-mono font-semibold text-error">
              -{formatUsdCents(Math.abs(row.amount_usd_cents))}
            </span>
            {t('failed_deductions.resolve_modal.body_suffix')}
          </p>
        </header>
        <fieldset className="flex flex-col gap-3">
          <legend className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
            {t('failed_deductions.resolve_modal.legend')}
          </legend>
          {RESOLUTIONS.map((option) => (
            <label
              key={option}
              className="flex items-start gap-3 rounded-lg bg-surface-container-low p-3"
            >
              <input
                type="radio"
                name="resolution"
                value={option}
                checked={choice === option}
                onChange={(e) => {
                  const next = e.currentTarget.value;
                  if (isResolution(next)) {
                    setChoice(next);
                  }
                }}
                data-testid={`failed-deduction-resolve-option-${option}`}
                disabled={resolve.isPending}
                className="mt-1 h-4 w-4 border-outline-variant bg-surface-container-lowest text-primary focus-visible:ring-2 focus-visible:ring-primary"
              />
              <span className="flex flex-col gap-1">
                <span className="text-sm font-semibold text-on-surface">
                  {t(`failed_deductions.resolve_modal.${option}`)}
                </span>
                <span className="text-[11px] text-outline">
                  {t(`failed_deductions.resolve_modal.${option}.hint`)}
                </span>
              </span>
            </label>
          ))}
        </fieldset>
        {error !== null ? (
          <p
            className="text-xs font-medium text-error"
            role="alert"
            data-testid="failed-deduction-resolve-error"
          >
            {error}
          </p>
        ) : null}
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => {
              if (!resolve.isPending) onClose();
            }}
            disabled={resolve.isPending}
            data-testid="failed-deduction-resolve-cancel"
            className="inline-flex items-center justify-center rounded-lg bg-surface-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright disabled:opacity-40 disabled:pointer-events-none"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={resolve.isPending}
            data-testid="failed-deduction-resolve-submit"
            className="inline-flex items-center justify-center rounded-lg bg-primary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] disabled:opacity-40 disabled:pointer-events-none"
          >
            {resolve.isPending
              ? t('failed_deductions.resolve_modal.submitting')
              : t('failed_deductions.resolve_modal.submit', {
                  choice: t(`failed_deductions.resolve_modal.${choice}`),
                })}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// Re-export the filter guard for consumers (and tests) that need to
// validate untrusted values before narrowing the tab state.
export { isResolvedFilter };
